import { getAppFromLastAppListByAppId, getProtectorApp } from '@protector/entities/company/__legacy__/redux.selectors';
import type { LicensingStatusAppsContractResponse, LicensingStatusAppsResponse } from '@protector/entities/company/models';
import { Entitlements } from '@protector/entities/licensing/constants';
import { hasEntitlements } from '@protector/entities/licensing/utils';
import { PROTECTOR_APP_ID } from 'config/constants';
import type { Selector } from 'core/core.selectors';
import { getSelectedCompanyId } from 'core/core.selectors';
import { compareStrings } from 'core/utils/strings';
import _ from 'lodash';
import moment from 'moment';
import type { AppState } from 'redux/app-state';
import { createSelector } from 'reselect';
import type { CompanyState } from './company.models';
import { selectAllCompanies, selectCompanyEntities } from './company.reducer';

export {
  getAppFromAppListByAppId,
  getAppFromLastAppListByAppId,
  getProtectorApp,
  hasRTRPlanEntitlement
} from '@protector/entities/company/__legacy__/redux.selectors';

const filterConfiguredApps = ({ contract: { start, end }, id }: LicensingStatusAppsResponse): boolean =>
  id === PROTECTOR_APP_ID && !!start && !!end;
const createGroupAppsByExpiration =
  (nowMoment: number) =>
  ({ contract: { end } }: LicensingStatusAppsResponse): 'activeApps' | 'inactiveApps' =>
    moment(end).diff(nowMoment) > 0 ? 'activeApps' : 'inactiveApps';

const sortAppsByStart = (
  { contract: { start: startA } }: LicensingStatusAppsResponse,
  { contract: { start: startB } }: LicensingStatusAppsResponse
): number => compareStrings(startB, startA);

export const selectMostCurrentApp: Selector<LicensingStatusAppsResponse | null> = createSelector(
  (state: AppState) => state.entities.company.licensingStatus?.apps,
  (apps: LicensingStatusAppsResponse[] | undefined) => {
    if (!apps) return null;

    const configuredApps = apps.filter(filterConfiguredApps);
    const { activeApps, inactiveApps } = _.groupBy(configuredApps, createGroupAppsByExpiration(moment.now()));

    if (activeApps?.length) {
      activeApps.sort(sortAppsByStart);
      return activeApps[0];
    }

    if (!inactiveApps?.length) {
      return null;
    }

    inactiveApps.sort(sortAppsByStart);
    return inactiveApps[0];
  }
);

export const getContract: Selector<LicensingStatusAppsContractResponse | undefined> = state => {
  const protectorApp = getProtectorApp(state);
  return protectorApp && hasEntitlements(protectorApp, [Entitlements.WARNING_EXPIRATION_PLAN]) ? protectorApp.contract : undefined;
};

export const getContractByAppId = createSelector(
  (state: AppState, appId: string) => ({
    appList: state.entities.company.licensingStatus?.apps,
    appId
  }),
  ({ appList, appId }) => {
    const app = getAppFromLastAppListByAppId(appList ?? [], appId);
    return app ? app.contract : undefined;
  }
);

const getCompanyEntities = (state: AppState) => selectCompanyEntities(state.entities.company);

export const getSelectedCompany = createSelector(getCompanyEntities, getSelectedCompanyId, (companies, selectedCompany) =>
  selectedCompany ? companies[selectedCompany] : undefined
);

export const getAllCompanies = (state: AppState) => selectAllCompanies(state.entities.company);

export const getLoadingCompany: Selector<CompanyState['isLoading']> = state => state.entities.company.isLoading;

export const getLoadingLicensingStatus: Selector<CompanyState['isLoadingLicensingStatus']> = state =>
  state.entities.company.isLoadingLicensingStatus;

export const getLogoCompanyById = createSelector(getCompanyEntities, getSelectedCompanyId, (companies, companyId) => {
  return companyId ? companies?.[companyId]?.picture_url : null;
});

const EMPTY_AVAILABLE_WORKSPACES: CompanyState['availableWorkspaces'] = [];

export const getAvailableWorkspaces: Selector<CompanyState['availableWorkspaces']> = state =>
  state.entities.company.availableWorkspaces ?? EMPTY_AVAILABLE_WORKSPACES;

export const getCurrentWorkspaceId = createSelector(
  (state: AppState) => state.entities.company.isLoadingCurrentWorkspaceId,
  (state: AppState) => state.entities.company.currentWorkspaceId,
  (loading, id) => {
    if (!loading) return id;
    return null;
  }
);

export const hasSimpleCerealsEntitlement: Selector<boolean> = createSelector(
  getProtectorApp,
  app => !!app && hasEntitlements(app, [Entitlements.SIMPLE_CEREALS])
);

export const selectHasDiseaseRiskLandingPage: Selector<boolean> = createSelector(
  getProtectorApp,
  hasSimpleCerealsEntitlement,
  (app, hasSimpleCereals) => (!!app && hasEntitlements(app, [Entitlements.DISEASE_RISK_LANDING_PAGE])) || hasSimpleCereals
);

export const hasHideMenuOptionWithoutSeason: Selector<boolean> = createSelector(
  getProtectorApp,
  app => !!app && hasEntitlements(app, [Entitlements.HIDE_MENU_OPTION_WITHOUT_SEASON])
);
